import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Image,
  Plus,
  Trash2,
  Download,
  Copy,
  Palette,
  FileText,
  ExternalLink,
  Upload,
  FolderOpen,
  Building2,
  Search,
  Eye,
  Edit,
  CheckCircle2,
  Link2
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import api from '../../lib/axios';

const SharedSupportsPage = () => {
  const { user, isAgency, isClient } = useAuth();
  const { theme } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [supports, setSupports] = useState([]);
  const [clients, setClients] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterClient, setFilterClient] = useState('all');
  const [activeTab, setActiveTab] = useState('logos');
  
  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedSupport, setSelectedSupport] = useState(null);
  const [uploading, setUploading] = useState(false);
  
  // Form state
  const [newSupport, setNewSupport] = useState({
    client_id: '',
    name: '',
    category: 'logo', // logo, logo_variant, charte, color, drive_link
    description: '',
    hex_colors: [],
    drive_link: '',
    file: null
  });
  const [newColor, setNewColor] = useState('#000000');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [supportsRes, clientsRes] = await Promise.all([
        api.get('/shared-supports/'),
        isAgency ? api.get('/users/?role=client') : Promise.resolve({ data: [] })
      ]);
      setSupports(supportsRes.data);
      if (isAgency) setClients(clientsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/svg+xml', 'application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Format non supporté. Utilisez PNG, JPG, SVG ou PDF.');
      return;
    }
    
    setNewSupport({ ...newSupport, file });
  };

  const handleCreate = async () => {
    if (!newSupport.name) {
      toast.error('Nom requis');
      return;
    }
    
    if (isAgency && !newSupport.client_id) {
      toast.error('Veuillez sélectionner un client');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('name', newSupport.name);
      formData.append('category', newSupport.category);
      formData.append('description', newSupport.description || '');
      formData.append('drive_link', newSupport.drive_link || '');
      formData.append('hex_colors', JSON.stringify(newSupport.hex_colors));
      if (newSupport.file) {
        formData.append('file', newSupport.file);
      }
      if (isAgency) {
        formData.append('client_id', newSupport.client_id);
      }
      
      await api.post('/shared-supports/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      toast.success('Support ajouté !');
      setShowCreateModal(false);
      setNewSupport({
        client_id: '',
        name: '',
        category: 'logo',
        description: '',
        hex_colors: [],
        drive_link: '',
        file: null
      });
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de l\'ajout');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Supprimer ce support ?')) return;
    
    try {
      await api.delete(`/shared-supports/${id}`);
      toast.success('Support supprimé');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleCopyColor = (color) => {
    navigator.clipboard.writeText(color);
    toast.success(`Couleur ${color} copiée !`);
  };

  const addColor = () => {
    if (newSupport.hex_colors.includes(newColor)) {
      toast.error('Cette couleur existe déjà');
      return;
    }
    setNewSupport({
      ...newSupport,
      hex_colors: [...newSupport.hex_colors, newColor]
    });
  };

  const removeColor = (color) => {
    setNewSupport({
      ...newSupport,
      hex_colors: newSupport.hex_colors.filter(c => c !== color)
    });
  };

  const categoryLabels = {
    logo: 'Logo principal',
    logo_variant: 'Déclinaison logo',
    charte: 'Charte graphique',
    color: 'Couleurs',
    drive_link: 'Lien Drive'
  };

  const filteredSupports = supports.filter(s => {
    const matchesSearch = s.name?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesClient = filterClient === 'all' || s.client_id === filterClient;
    const matchesCategory = activeTab === 'all' || s.category === activeTab || 
                           (activeTab === 'logos' && (s.category === 'logo' || s.category === 'logo_variant'));
    return matchesSearch && matchesClient && matchesCategory;
  });

  // Group supports by client for display
  const supportsByClient = filteredSupports.reduce((acc, support) => {
    const clientId = support.client_id || 'unknown';
    if (!acc[clientId]) {
      acc[clientId] = {
        client: clients.find(c => c.id === clientId) || { company_name: 'Mon entreprise' },
        items: []
      };
    }
    acc[clientId].items.push(support);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar 
        collapsed={collapsed} 
        setCollapsed={setCollapsed}
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
      />
      <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
      
      <main className={cn(
        "transition-all duration-300 px-6 pb-6",
        collapsed ? "md:ml-20" : "md:ml-64"
      )}
        style={{ paddingTop: "130px" }}
      >
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-xl font-bold text-[#1a365d] flex items-center gap-2">
              <Image className="w-5 h-5" />
              Supports de communication
            </h1>
            <p className="text-sm text-[#64748b]">
              Logos, chartes graphiques, couleurs et ressources partagées
            </p>
          </div>
          
          <Button 
            onClick={() => setShowCreateModal(true)}
            size="sm"
            style={{ backgroundColor: theme.colors?.accent }}
            className="text-white hover:opacity-90"
          >
            <Plus className="w-4 h-4 mr-1" />
            {isAgency ? 'Ajouter un support' : 'Déposer un fichier'}
          </Button>
        </div>

        {/* Tabs - horizontal scroll on mobile */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-4 sm:mb-6">
          <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
            <TabsList className="bg-white border inline-flex w-max sm:w-auto">
              <TabsTrigger value="logos" className="text-xs sm:text-sm px-2 sm:px-4">
                <Image className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" /> Logos
              </TabsTrigger>
              <TabsTrigger value="charte" className="text-xs sm:text-sm px-2 sm:px-4">
                <FileText className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" /> Chartes
              </TabsTrigger>
              <TabsTrigger value="color" className="text-xs sm:text-sm px-2 sm:px-4">
                <Palette className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" /> Couleurs
              </TabsTrigger>
              <TabsTrigger value="drive_link" className="text-xs sm:text-sm px-2 sm:px-4">
                <Link2 className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" /> Drive
              </TabsTrigger>
              <TabsTrigger value="all" className="text-xs sm:text-sm px-2 sm:px-4">Tout</TabsTrigger>
            </TabsList>
          </div>
        </Tabs>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#64748b]" />
            <Input
              placeholder="Rechercher..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 input-default"
            />
          </div>
          
          {isAgency && clients.length > 0 && (
            <Select value={filterClient} onValueChange={setFilterClient}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Client" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les clients</SelectItem>
                {clients.map(client => (
                  <SelectItem key={client.id} value={client.id}>
                    {client.company_name || `${client.first_name} ${client.last_name}`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="spinner" />
          </div>
        ) : Object.keys(supportsByClient).length === 0 ? (
          <Card className="card">
            <CardContent className="p-12 text-center">
              <FolderOpen className="w-16 h-16 mx-auto mb-4 text-[#94a3b8]" />
              <h3 className="text-lg font-semibold text-[#1a365d] mb-2">Aucun support</h3>
              <p className="text-[#64748b] mb-4">
                {isAgency ? 'Ajoutez des supports de communication pour vos clients' : 'Déposez vos logos, chartes et ressources graphiques ici'}
              </p>
              <Button onClick={() => setShowCreateModal(true)} className="btn-primary">
                <Plus className="w-4 h-4 mr-2" />
                {isAgency ? 'Ajouter un support' : 'Déposer un fichier'}
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-8">
            {Object.entries(supportsByClient).map(([clientId, { client, items }]) => (
              <div key={clientId}>
                {isAgency && (
                  <div className="flex items-center gap-2 mb-4">
                    <Building2 className="w-5 h-5 text-[#64748b]" />
                    <h2 className="text-lg font-semibold text-[#1a365d]">
                      {client.company_name || `${client.first_name || ''} ${client.last_name || ''}`}
                    </h2>
                    <Badge className="bg-slate-100 text-slate-600">{items.length} supports</Badge>
                  </div>
                )}
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {items.map(support => (
                    <Card 
                      key={support.id} 
                      className="card card-hover overflow-hidden"
                    >
                      {/* Preview */}
                      {support.file_url && support.category !== 'color' && (
                        <div className="aspect-video bg-slate-100 flex items-center justify-center overflow-hidden">
                          <img 
                            src={support.file_url} 
                            alt={support.name}
                            className="max-w-full max-h-full object-contain"
                          />
                        </div>
                      )}
                      
                      {/* Color preview */}
                      {support.category === 'color' && support.hex_colors?.length > 0 && (
                        <div className="h-20 flex">
                          {support.hex_colors.map((color, idx) => (
                            <div 
                              key={idx}
                              className="flex-1 cursor-pointer hover:opacity-80 transition-opacity relative group"
                              style={{ backgroundColor: color }}
                              onClick={() => handleCopyColor(color)}
                            >
                              <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 bg-black/30 transition-opacity">
                                <Copy className="w-4 h-4 text-white" />
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-2">
                          <div>
                            <h4 className="font-semibold text-[#1a365d]">{support.name}</h4>
                            <Badge className="mt-1 bg-slate-100 text-slate-600 text-xs">
                              {categoryLabels[support.category]}
                            </Badge>
                          </div>
                        </div>
                        
                        {support.description && (
                          <p className="text-sm text-[#64748b] mt-2 line-clamp-2">{support.description}</p>
                        )}
                        
                        {/* Color codes */}
                        {support.category === 'color' && support.hex_colors?.length > 0 && (
                          <div className="mt-3 flex flex-wrap gap-1">
                            {support.hex_colors.map((color, idx) => (
                              <button
                                key={idx}
                                onClick={() => handleCopyColor(color)}
                                className="px-2 py-1 text-xs font-mono bg-slate-100 rounded hover:bg-slate-200 transition-colors flex items-center gap-1"
                              >
                                <div className="w-3 h-3 rounded" style={{ backgroundColor: color }} />
                                {color}
                              </button>
                            ))}
                          </div>
                        )}
                        
                        {/* Drive link */}
                        {support.drive_link && (
                          <a 
                            href={support.drive_link}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mt-3 flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700"
                          >
                            <ExternalLink className="w-4 h-4" />
                            Ouvrir dans Drive
                          </a>
                        )}
                        
                        {/* Actions */}
                        <div className="flex items-center gap-2 mt-4 pt-3 border-t border-[#e2e8f0]">
                          {support.file_url && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => window.open(support.file_url, '_blank')}
                            >
                              <Download className="w-4 h-4 mr-1" />
                              Télécharger
                            </Button>
                          )}
                          {isAgency && (
                            <Button 
                              size="sm" 
                              variant="ghost"
                              className="text-red-500 hover:text-red-600 hover:bg-red-50"
                              onClick={() => handleDelete(support.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Create Modal */}
        <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Image className="w-5 h-5 text-purple-500" />
                Ajouter un support
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {isAgency && (
                <div className="space-y-2">
                  <Label>Client *</Label>
                  <Select 
                    value={newSupport.client_id} 
                    onValueChange={(v) => setNewSupport({...newSupport, client_id: v})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner un client" />
                    </SelectTrigger>
                    <SelectContent>
                      {clients.map(client => (
                        <SelectItem key={client.id} value={client.id}>
                          <span className="flex items-center gap-2">
                            <Building2 className="w-4 h-4" />
                            {client.company_name || `${client.first_name} ${client.last_name}`}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="space-y-2">
                <Label>Nom *</Label>
                <Input
                  value={newSupport.name}
                  onChange={(e) => setNewSupport({...newSupport, name: e.target.value})}
                  placeholder="Ex: Logo principal, Charte 2024..."
                  className="input-default"
                />
              </div>

              <div className="space-y-2">
                <Label>Type</Label>
                <Select 
                  value={newSupport.category} 
                  onValueChange={(v) => setNewSupport({...newSupport, category: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="logo">Logo principal</SelectItem>
                    <SelectItem value="logo_variant">Déclinaison logo</SelectItem>
                    <SelectItem value="charte">Charte graphique</SelectItem>
                    <SelectItem value="color">Couleurs</SelectItem>
                    <SelectItem value="drive_link">Lien Drive externe</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* File upload for non-color/link types */}
              {!['color', 'drive_link'].includes(newSupport.category) && (
                <div className="space-y-2">
                  <Label>Fichier</Label>
                  <div className="border-2 border-dashed border-[#e2e8f0] rounded-lg p-6 text-center hover:border-[#00d4aa] transition-colors">
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/svg+xml,application/pdf"
                      onChange={handleFileUpload}
                      className="hidden"
                      id="file-upload"
                    />
                    <label htmlFor="file-upload" className="cursor-pointer">
                      {newSupport.file ? (
                        <div className="flex items-center justify-center gap-2">
                          <CheckCircle2 className="w-5 h-5 text-emerald-500" />
                          <span className="text-[#1a365d] font-medium">{newSupport.file.name}</span>
                        </div>
                      ) : (
                        <>
                          <Upload className="w-8 h-8 mx-auto mb-2 text-[#64748b]" />
                          <p className="text-sm text-[#64748b]">
                            Cliquez ou déposez un fichier<br />
                            PNG, JPG, SVG ou PDF
                          </p>
                        </>
                      )}
                    </label>
                  </div>
                </div>
              )}

              {/* Color picker */}
              {newSupport.category === 'color' && (
                <div className="space-y-2">
                  <Label>Couleurs</Label>
                  <div className="flex gap-2">
                    <input
                      type="color"
                      value={newColor}
                      onChange={(e) => setNewColor(e.target.value)}
                      className="w-12 h-10 rounded cursor-pointer"
                    />
                    <Input
                      value={newColor}
                      onChange={(e) => setNewColor(e.target.value)}
                      placeholder="#000000"
                      className="input-default flex-1 font-mono"
                    />
                    <Button type="button" onClick={addColor} variant="outline">
                      <Plus className="w-4 h-4" />
                    </Button>
                  </div>
                  
                  {newSupport.hex_colors.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {newSupport.hex_colors.map((color, idx) => (
                        <div 
                          key={idx}
                          className="flex items-center gap-1 px-2 py-1 bg-slate-100 rounded"
                        >
                          <div className="w-4 h-4 rounded" style={{ backgroundColor: color }} />
                          <span className="text-xs font-mono">{color}</span>
                          <button 
                            onClick={() => removeColor(color)}
                            className="text-red-500 hover:text-red-600"
                          >
                            <Trash2 className="w-3 h-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Drive link */}
              {newSupport.category === 'drive_link' && (
                <div className="space-y-2">
                  <Label>Lien Drive</Label>
                  <Input
                    value={newSupport.drive_link}
                    onChange={(e) => setNewSupport({...newSupport, drive_link: e.target.value})}
                    placeholder="https://drive.google.com/..."
                    className="input-default"
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label>Description (optionnel)</Label>
                <Textarea
                  value={newSupport.description}
                  onChange={(e) => setNewSupport({...newSupport, description: e.target.value})}
                  placeholder="Notes ou instructions..."
                  className="input-default"
                  rows={3}
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateModal(false)}>
                Annuler
              </Button>
              <Button 
                onClick={handleCreate} 
                disabled={uploading}
                style={{ backgroundColor: theme.colors?.accent }}
                className="text-white hover:opacity-90"
              >
                {uploading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2" />
                    Upload...
                  </>
                ) : (
                  <>
                    <Plus className="w-4 h-4 mr-2" />
                    Ajouter
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
};

export default SharedSupportsPage;
